import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Grid, Paper, Skeleton, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { TREND_COLORS } from './maps/mapColorScales';
import { CountUp, Reveal, useGrowIn } from './landingMotion';
import {
  categoryForComponent,
  formatCount,
  formatCoveragePercent,
  formatSignedPercent,
  travelPhrase,
} from './landingHighlights';
import { defaultWeights } from './opportunity_calculator/indicatorConfig';
import { getRiskDataset } from './risk_calculator/datasets/riskDatasets';
import { buildRiskIndicators } from './risk_calculator/riskIndicatorConfig';
import { scoreRiskSchools } from './risk_calculator/riskTiers';
import useRiskCalculatorData from './risk_calculator/useRiskCalculatorData';

const GRADE_COLORS = { A: '#1a9850', B: '#91cf60', C: '#fee08b', D: '#fc8d59', F: '#d73027' };
const TOP_COUNT = 5;

/**
 * Top counties by default-weight opportunity score, names only (scores stay
 * in the calculator). Falls back to districts for locations whose calculator
 * has no county tier. The dataset module is loaded on demand so the landing
 * page doesn't bundle every state's calculator JSON.
 */
function useTopOpportunityAreas(locationId) {
  const [state, setState] = useState({ loading: true, rows: [], levelId: null });
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      import('./opportunity_calculator/datasets'),
      import('./opportunity_calculator/scoringEngine'),
      import('./opportunity_calculator/indicatorConfig'),
    ])
      .then(([datasets, engine, config]) => {
        const dataset = datasets.getCalculatorDataset(locationId);
        if (cancelled) return;
        if (dataset.id !== locationId) {
          setState({ loading: false, rows: [], levelId: null });
          return;
        }
        const levelId = dataset.levels.county ? 'county' : dataset.levels.district ? 'district' : null;
        if (!levelId) {
          setState({ loading: false, rows: [], levelId: null });
          return;
        }
        const indicators = dataset.levels[levelId].indicators;
        const rows = engine
          .rankEntities(datasets.entitiesForLevel(dataset, levelId), indicators, config.defaultWeights(indicators), {
            minEnrollment: config.DEFAULT_MIN_ENROLLMENT,
          })
          .slice(0, TOP_COUNT)
          .map((row) => row.entity);
        setState({ loading: false, rows, levelId });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, rows: [], levelId: null });
      });
    return () => {
      cancelled = true;
    };
  }, [locationId]);
  return state;
}

/** Highest default-weight risk tiers among charters, names and tiers only. */
function useHighestRiskCharters(locationId) {
  const dataset = getRiskDataset(locationId);
  const { loading, meta, charters } = useRiskCalculatorData(locationId, { enabled: Boolean(dataset) });
  const rows = useMemo(() => {
    if (!dataset) return [];
    const indicators = buildRiskIndicators(dataset, meta);
    return scoreRiskSchools(charters, indicators, defaultWeights(indicators))
      .filter((row) => row.points !== null)
      .slice(0, TOP_COUNT);
  }, [dataset, meta, charters]);
  return { available: Boolean(dataset), loading: Boolean(dataset) && loading, rows };
}

function StorySection({ step, title, lead, category, onSelectCategory, children }) {
  return (
    <Reveal>
      <Paper
        elevation={0}
        data-testid={`landing-story-${step}`}
        sx={{ p: { xs: 2.5, md: 3 }, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
          {step}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.75 }}>
          {title}
        </Typography>
        {lead && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
            {lead}
          </Typography>
        )}
        {children}
        {category && (
          <Button
            size="small"
            endIcon={<ChevronRightIcon />}
            onClick={() => onSelectCategory(category)}
            sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600, px: 0 }}
          >
            Explore the map
          </Button>
        )}
      </Paper>
    </Reveal>
  );
}

function BigNumber({ value, label }) {
  return (
    <Box>
      <Typography variant="h4" component="p" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.1 }}>
        {value === null || value === undefined ? '—' : <CountUp value={value} />}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/** One horizontal stacked bar with a labeled key underneath. */
function StackedBar({ parts, testId }) {
  const ref = useRef(null);
  const { animate, grown } = useGrowIn(ref);
  const total = parts.reduce((sum, part) => sum + (part.value || 0), 0);
  if (!total) return null;
  return (
    <Box data-testid={testId} sx={{ mb: 1 }}>
      <Box ref={ref} sx={{ display: 'flex', height: 14, borderRadius: 1, overflow: 'hidden', bgcolor: '#eee' }}>
        {parts
          .filter((part) => part.value > 0)
          .map((part) => (
            <Box
              key={part.label}
              sx={{
                width: grown ? `${(part.value / total) * 100}%` : 0,
                bgcolor: part.color,
                transition: animate ? 'width 900ms cubic-bezier(0.22, 1, 0.36, 1)' : undefined,
              }}
            />
          ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mt: 0.75 }}>
        {parts.map((part) => (
          <Typography key={part.label} variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: part.color, mr: 0.75 }} />
            {`${part.label}: ${part.value.toLocaleString()}`}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

/** Thin progress bar + count for one sector's enrollment-weighted A/B share. */
function QualityShareRow({ label, share }) {
  const ref = useRef(null);
  const { animate, grown } = useGrowIn(ref);
  if (!share || share.pct === null || share.pct === undefined || !share.studentsRated) return null;
  const pct = formatCoveragePercent(share.pct);
  return (
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {pct}
        </Typography>
      </Box>
      <Box ref={ref} sx={{ height: 10, borderRadius: 1, overflow: 'hidden', bgcolor: '#eee' }}>
        <Box
          sx={{
            height: '100%',
            width: grown ? pct : 0,
            bgcolor: '#1a4a8a',
            transition: animate ? 'width 900ms cubic-bezier(0.22, 1, 0.36, 1)' : undefined,
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {`${Math.round(share.studentsQuality).toLocaleString()} of ${Math.round(share.studentsRated).toLocaleString()} students`}
      </Typography>
    </Box>
  );
}

function trendParts(counts, { newLabel = null } = {}) {
  if (!counts?.total) return [];
  const parts = [
    { label: 'Growing', value: counts.growing, color: TREND_COLORS.Growing },
    { label: 'Stable', value: counts.stable, color: TREND_COLORS.Stable },
    { label: 'Declining', value: counts.declining, color: TREND_COLORS.Declining },
  ];
  const untracked = counts.total - counts.growing - counts.stable - counts.declining;
  if (newLabel && untracked > 0) parts.push({ label: newLabel, value: untracked, color: '#bdbdbd' });
  return parts;
}

function RankedList({ loading, rows, render, empty }) {
  if (loading) return <Skeleton variant="rounded" height={120} />;
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
      {rows.map((row, index) => (
        <Reveal component="li" key={index} delay={index * 90} sx={{ mb: 0.5 }}>
          {render(row)}
        </Reveal>
      ))}
    </Box>
  );
}

/**
 * Scrolling story for statewide letter-grade locations (Arkansas): six short
 * sections, each linked to the map that explains it. Rankings show names and
 * tiers only; opportunity and risk scores stay in the calculators.
 */
export default function LandingStory({ location, summary, onSelectCategory }) {
  const locationId = location?.id;
  const name = String(location?.name || '').trim() || 'This state';
  const schools = summary?.schools;
  const config = summary?.stateConfig || {};
  const opportunity = useTopOpportunityAreas(locationId);
  const risk = useHighestRiskCharters(locationId);
  if (!schools) return null;

  const dynamics = categoryForComponent(location, 'StateDynamicsMap');
  const quality = categoryForComponent(location, 'SchoolLocationsMap', 'quality');
  const charters = categoryForComponent(location, 'SchoolLocationsMap', 'charter');
  const opportunityMap = categoryForComponent(location, 'DistrictOpportunityMap');
  const riskMap = categoryForComponent(location, 'CharterEnrollmentRiskMap');
  const k12 = summary.k12;
  const years = summary.years || {};
  const tiers = schools.quality || {};
  const grades = tiers.letterGrades || {};
  const footprint = schools.charterFootprint || {};
  const charterCoverage = summary.deserts?.charter?.childrenEstimate;
  const qualityCoverage = summary.deserts?.districtRingsLoaded ? summary.deserts?.quality?.childrenEstimate : null;
  const travel = travelPhrase(config);
  const rated = (tiers.quality || 0) + (tiers.notQuality || 0);

  return (
    <Box sx={{ mb: 4 }} data-testid="landing-story">
      <Box sx={{ mb: 2, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark', lineHeight: 1.3 }}>
          {`The ${name} Story`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
          Five questions about where children live, how schools are changing, and where new options could help.
        </Typography>
      </Box>

      <StorySection
        step="Children, schools, and districts"
        title={
          formatCount(k12?.current) ? (
            <>
              <CountUp value={formatCount(k12.current)} /> school-age children
            </>
          ) : (
            'Thousands of school-age children'
          )
        }
        lead={
          k12?.change !== null && k12?.change !== undefined
            ? `The K-12 population is projected to change ${formatSignedPercent(k12.change)}${
                k12.futureYear ? ` by ${k12.futureYear}` : ''
              }.`
            : null
        }
        category={dynamics}
        onSelectCategory={onSelectCategory}
      >
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <BigNumber value={schools.total.toLocaleString()} label="public schools" />
          </Grid>
          <Grid item xs={6} md={3}>
            <BigNumber value={schools.district.toLocaleString()} label="district-run schools" />
          </Grid>
          <Grid item xs={6} md={3}>
            <BigNumber value={schools.charter.toLocaleString()} label="charter schools" />
          </Grid>
          <Grid item xs={6} md={3}>
            <BigNumber value={summary.districts?.total?.toLocaleString()} label="school districts" />
          </Grid>
        </Grid>
      </StorySection>

      <StorySection
        step="Growing and declining areas"
        title={
          summary.counties?.total
            ? `${summary.counties.declining} of ${summary.counties.total} counties are projected to lose school-age children`
            : 'Where the school-age population is changing'
        }
        lead={
          schools.declining?.eligible
            ? `${formatCoveragePercent(schools.declining.pctDeclining10)} of schools open since ${
                schools.comparisonYear || years.comparisonYear || 'five years ago'
              } lost 10% or more of their enrollment.`
            : null
        }
        category={dynamics}
        onSelectCategory={onSelectCategory}
      >
        {summary.counties?.total > 0 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {`Counties by projected K-12 change${k12?.futureYear ? ` to ${k12.futureYear}` : ''}`}
            </Typography>
            <StackedBar testId="story-county-trend" parts={trendParts(summary.counties)} />
          </>
        )}
        {summary.districts?.total > 0 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              Districts by projected K-12 change
            </Typography>
            <StackedBar testId="story-district-trend" parts={trendParts(summary.districts)} />
          </>
        )}
      </StorySection>

      <StorySection
        step="Quality schools"
        title={
          rated
            ? `${tiers.quality.toLocaleString()} of ${rated.toLocaleString()} rated schools earned ${
                config.qualityLabel ? `an ${config.qualityLabel}` : 'a quality rating'
              }`
            : 'School quality'
        }
        lead={
          qualityCoverage?.children
            ? `About ${formatCoveragePercent(qualityCoverage.childrenCoveredPct)} of ${
                summary.deserts?.gradeBand === 'K5' ? 'K–5 ' : ''
              }children live within a ${travel} of a quality school.${
                tiers.accountabilityYear ? ` Ratings are from ${tiers.accountabilityYear}.` : ''
              }`
            : tiers.accountabilityYear
              ? `Ratings are from ${tiers.accountabilityYear}.`
              : null
        }
        category={quality}
        onSelectCategory={onSelectCategory}
      >
        <StackedBar
          testId="story-letter-grades"
          parts={Object.keys(GRADE_COLORS).map((grade) => ({
            label: grade,
            value: grades[grade] || 0,
            color: GRADE_COLORS[grade],
          }))}
        />
        {tiers.unrated > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`${tiers.unrated} schools have no letter grade.`}
          </Typography>
        )}
        <Box sx={{ mt: 2 }}>
          <QualityShareRow label="Charter students in an A/B school" share={tiers.charterStudents} />
          <QualityShareRow label="District students in an A/B school" share={tiers.districtStudents} />
        </Box>
      </StorySection>

      <StorySection
        step="Charter footprint"
        title={`${schools.charter.toLocaleString()} charter schools${
          footprint.enrollment ? ` enroll ${formatCount(footprint.enrollment)} students` : ''
        }${
          footprint.enrollmentChange !== null && footprint.enrollmentChange !== undefined && schools.comparisonYear
            ? `, ${footprint.enrollmentChange < 0 ? 'down' : 'up'} ${Math.round(
                Math.abs(footprint.enrollmentChange) * 100
              )}% since ${schools.comparisonYear}`
            : ''
        }`}
        lead={
          charterCoverage?.children
            ? `About ${formatCoveragePercent(charterCoverage.childrenCoveredPct)} of ${
                summary.deserts?.gradeBand === 'K5' ? 'K–5 ' : ''
              }children live within a ${travel} of a charter school.${
                footprint.enrollmentSharePct !== null && footprint.enrollmentSharePct !== undefined
                  ? ` Charter schools serve ${formatCoveragePercent(footprint.enrollmentSharePct)} of ${name}'s public school students.`
                  : ''
              }`
            : null
        }
        category={charters}
        onSelectCategory={onSelectCategory}
      >
        <StackedBar
          testId="story-charter-age"
          parts={[
            { label: 'Steady state', value: footprint.steadyState || 0, color: '#1a4a8a' },
            { label: 'New or phasing', value: footprint.newOrPhasing || 0, color: '#f28e2b' },
          ]}
        />
        {footprint.total > 0 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {`Charters by enrollment trend${schools.comparisonYear ? ` since ${schools.comparisonYear}` : ''}`}
            </Typography>
            <StackedBar
              testId="story-charter-trend"
              parts={trendParts(footprint, { newLabel: 'Too new to compare' })}
            />
          </>
        )}
      </StorySection>

      {opportunityMap && (
        <StorySection
          step="Opportunity areas"
          title={`Top ${TOP_COUNT} ${opportunity.levelId === 'district' ? 'districts' : 'counties'} for new school options`}
          lead={`Ranked with the Opportunity Calculator's default weights (${
            opportunity.levelId === 'district' ? 'districts' : 'counties'
          } with at least 1,000 students). Adjust the weights in the calculator.`}
          category={opportunityMap}
          onSelectCategory={onSelectCategory}
        >
          <RankedList
            loading={opportunity.loading}
            rows={opportunity.rows}
            empty="Opportunity rankings are not available for this location."
            render={(area) => (
              <Typography variant="body2">
                <b>{area.name}</b>
                {formatCount(area.k12Population2026)
                  ? ` · ${formatCount(area.k12Population2026)} school-age children`
                  : ''}
                {area.k12Change2026_2031 !== null && area.k12Change2026_2031 !== undefined
                  ? ` (${formatSignedPercent(area.k12Change2026_2031)} projected)`
                  : ''}
              </Typography>
            )}
          />
        </StorySection>
      )}

      {riskMap && risk.available && (
        <StorySection
          step="Enrollment risk"
          title="Charters facing the highest enrollment risk"
          lead="Risk tiers use the Risk Calculator's default weights."
          category={riskMap}
          onSelectCategory={onSelectCategory}
        >
          <RankedList
            loading={risk.loading}
            rows={risk.rows}
            empty="Risk tiers are not available yet."
            render={(row) => (
              <Typography variant="body2">
                <b>{row.school.name || row.school.schoolName}</b>
                {row.school.city ? ` · ${row.school.city}` : ''}
                {' · '}
                <Box component="span" sx={{ color: row.tier?.color, fontWeight: 600 }}>
                  {`${row.tier?.label} risk`}
                </Box>
              </Typography>
            )}
          />
        </StorySection>
      )}
    </Box>
  );
}
